import React, { Component } from "react"
import * as firebase from "firebase"
import "./App.css"
import {
	Button,
	Col,
	FormControl,
	Jumbotron,
	Panel,
	Grid,
	Row,
	OverlayTrigger,
	Tooltip,
	Well,
	ProgressBar,
	Modal,
} from "react-bootstrap"
import demoUser from "./demoUser.json"
import HoldingsTable from "./HoldingsTable.js"
import Accounts from "./Accounts"
import request from "./request"
import RefreshStatus from "./RefreshStatus"
import { formatPrice, formatGain } from "./format"
import FAQ from "./FAQ"
import PlanExpiredModal from "./PlanExpiredModal"
import UpgradePanel from "./UpgradePanel"
import moment from "moment"

const TRIAL_LENGTH_DAYS = 10

export default class App extends Component {
	constructor() {
		super()
		this.state = {
			portfolioMarketValue: 9909.3425,
			portfolioBookValue: 10096.133,
			holdingsFilter: "",
			holdings: JSON.parse(JSON.stringify(demoUser.portfolio.accounts)), // Deep clone
			refreshStatus: null,
			isLoaded: false,
			isCompact: isSmallWindow(),
			showPlanExpiredModal: false,
		}
		this.unsubscribePortfolio = null
	}

	componentDidMount() {
		this.loadData()
		this.processPlanExpired()
		window.addEventListener("resize", this.handleWindowResize)
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.uid !== prevProps.uid) {
			this.loadData()
		}
		if (this.props.showRenewMessage !== prevProps.showRenewMessage) {
			this.processPlanExpired()
		}
	}

	componentWillUnmount() {
		if (this.unsubscribePortfolio !== null) {
			this.unsubscribePortfolio()
		}
		window.removeEventListener("resize", this.handleWindowResize)
	}

	async loadData() {
		const { uid } = this.props
		if (uid === null) {
			await this.loadDemoData()
		} else {
			await this.loadUserData()
		}
	}

	async loadDemoData() {
		const { assets } = demoUser.portfolio.accounts.demo
		const fsyms = Object.keys(assets).join()
		const res = await window.fetch(
			`https://min-api.cryptocompare.com/data/pricemulti?fsyms=${fsyms}&tsyms=USD`
		)
		const prices = await res.json()
		const holdings = {
			demo: {
				assets: {},
				name: "Demo",
			},
		}
		let portfolioMarketValue = 0
		let portfolioBookValue = 0
		for (const asset of Object.keys(assets)) {
			const price = prices[asset].USD
			const quantity = assets[asset].quantity
			const marketValue = quantity * price
			portfolioMarketValue += marketValue
			const averageCost = assets[asset].averageCost
			const bookValue = quantity * averageCost
			portfolioBookValue += bookValue
			holdings.demo.assets[asset] = {
				name: asset,
				quantity,
				price,
				marketValue,
				averageCost,
				bookValue,
				quote: "USD",
			}
		}
		for (const asset of Object.keys(holdings.demo.assets)) {
			holdings.demo.assets[asset].portfolioPercent =
				holdings.demo.assets[asset].marketValue / portfolioMarketValue
		}
		this.setState({
			portfolioMarketValue,
			portfolioBookValue,
			holdings,
		})
	}

	async loadUserData() {
		const { uid } = this.props
		this.handleRefresh({
			force: false,
		})
		const db = firebase.firestore()
		if (this.unsubscribePortfolio !== null) {
			this.unsubscribePortfolio()
		}
		this.unsubscribePortfolio = db
			.collection("portfolios")
			.doc(uid)
			.onSnapshot(doc => {
				if (!doc.exists) {
					this.setState({
						portfolioMarketValue: 0,
						portfolioBookValue: 0,
						holdings: {},
						isLoaded: true,
					})
					return
				}
				const portfolio = doc.data()
				const accounts = portfolio.accounts
				if (accounts !== undefined) {
					this.setState({
						portfolioMarketValue: portfolio.marketValue,
						portfolioBookValue: portfolio.bookValue,
						holdings: accounts,
						isLoaded: true,
					})
				}
			})
	}

	render() {
		const {
			portfolioMarketValue,
			portfolioBookValue,
			refreshStatus,
			isLoaded,
			isCompact,
			showPlanExpiredModal,
		} = this.state
		const { uid, price, planExpiresAt, upgradeMessage } = this.props
		const isSignedIn = uid !== null
		const headerMessage =
			"Automatically track cryptocurrency gain and portfolio value for Kraken, Bittrex, and GDAX."
		const isPremium = this.isPremium()
		return (
			<div className="App">
				{refreshStatus ? (
					<RefreshStatus {...refreshStatus} onRefresh={this.handleRefresh} />
				) : null}
				{!isSignedIn ? (
					<Jumbotron>
						<h1>Crypto Portfolio Tracker</h1>
						<p>{headerMessage}</p>
						<p>
							<Button
								bsStyle="primary"
								bsSize={!isCompact ? "large" : null}
								href="#accounts-panel"
								tabIndex="1"
								data-scroll
								data-on="click"
								data-event-category="Get Started"
								data-event-action="click"
							>
								Get started with Crypto Portfolio Tracker
							</Button>
						</p>
					</Jumbotron>
				) : null}
				{uid && !isLoaded ? (
					<Well>
						<p>Fetching portfolio...</p>
						<ProgressBar active now={100} />
					</Well>
				) : (
					<div>
						<Panel header={`Portfolio Overview ${isSignedIn ? "" : "(Demo)"}`}>
							<Grid>
								<Row className="overview-row">
									<Col xs={4}>
										<p>
											<b>Market Value</b>
										</p>
										<p>{formatPrice("USD", portfolioMarketValue, true)}</p>
									</Col>
									<Col xs={4}>
										<p>
											<b>Book Value</b>
										</p>
										<p>{formatPrice("USD", portfolioBookValue, isPremium)}</p>
									</Col>
									<Col xs={4}>
										<p>
											<b>Unr. Gain</b>
										</p>
										<p>
											{formatGain(
												"USD",
												portfolioMarketValue,
												portfolioBookValue,
												isPremium
											)}
										</p>
									</Col>
								</Row>
							</Grid>
						</Panel>
						<Panel header={this.renderHoldingsFilter()}>
							<HoldingsTable
								uid={uid}
								holdings={Object.assign({}, this.state.holdings)}
								holdingsFilter={this.state.holdingsFilter}
								isPremium={isPremium}
								isSignedIn={isSignedIn}
							/>
						</Panel>
					</div>
				)}
				<Panel header="Accounts" id="accounts-panel">
					<Accounts uid={uid} />
				</Panel>
				{this.renderUpgradePanel()}
				<Panel header="FAQ">
					<FAQ />
				</Panel>
				<Modal bsSize="large" show={showPlanExpiredModal} enforceFocus={false}>
					<PlanExpiredModal
						uid={uid}
						onPurchase={this.handlePurchase}
						planExpiresAt={planExpiresAt}
						price={price}
						upgradeMessage={upgradeMessage}
					/>
				</Modal>
			</div>
		)
	}

	renderHoldingsFilter() {
		return (
			<div>
				<span>Holdings</span>
				{this.isPremium() ? (
					<FormControl
						className="filter-input"
						type="text"
						placeholder="Filter Assets"
						value={this.state.holdingsFilter}
						onChange={this.handleFilterHoldingsChange}
					/>
				) : (
					<OverlayTrigger
						placement="bottom"
						overlay={
							<Tooltip id="upgrade-required">
								<span>Get Pro to filter assets.</span>
							</Tooltip>
						}
					>
						<FormControl
							className="filter-input"
							type="text"
							placeholder="Filter Assets"
							value=""
							disabled
						/>
					</OverlayTrigger>
				)}
			</div>
		)
	}

	renderUpgradePanel() {
		const { plan, planExpiresAt } = this.props
		const { portfolioMarketValue } = this.state
		if (!plan || !planExpiresAt || !portfolioMarketValue) {
			return
		}
		const now = moment()
		const expiresAt = moment(planExpiresAt)
		const expiresInDays = expiresAt.diff(now, "days")
		if (plan === "free" || expiresInDays < TRIAL_LENGTH_DAYS) {
			return (
				<Panel header="Upgrade Now">
					<UpgradePanel />
				</Panel>
			)
		}
	}

	handleFilterHoldingsChange = event => {
		this.setState({
			holdingsFilter: event.target.value,
		})
	}

	handleRefresh = ({ force = true } = {}) => {
		request(
			this.props.uid,
			{ action: "refreshPortfolios", payload: { force } },
			data => {
				this.setState({
					refreshStatus: data,
				})
			}
		).catch(error => {
			this.setState({
				refreshStatus: {
					errors: {
						timeout: `Couldn't refresh your portfolio: ${error.message}`,
					},
				},
			})
			throw error
		})
	}

	isPremium() {
		const { uid, plan } = this.props
		return (
			uid === null ||
			plan === "standard" ||
			plan === "pro" ||
			plan === "ultimate" ||
			plan === "classic"
		)
	}

	handleWindowResize = event => {
		this.setState({
			isCompact: isSmallWindow(),
		})
	}

	processPlanExpired() {
		const { showRenewMessage } = this.props
		this.setState({
			showPlanExpiredModal: showRenewMessage,
		})
	}
}

function isSmallWindow() {
	return window.innerWidth < 480
}



// WEBPACK FOOTER //
// ./src/App.js